import React, { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import { createAccountService } from "../services/apiService";

import { Layout } from "@/components/Layout"
import { Toaster } from "@/components/ui/toaster"
import { useToast } from "@/hooks/useToast"
import { TrialAccountsPane } from "@/components/TrialAccountsPane";
import { AuthorizationModal } from "@/components/AuthorizationModal";

interface Account {
    storeUrl: string;
    expiresAt: string;
    hooksCount: number;
    usage: number;
}

export const ExpiredTrials: React.FC = () => {
    const api = useApi();
    const { toast } = useToast();
    const accountService = createAccountService(api);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeMenuItem, setActiveMenuItem] = useState("expired-trials");

    // State for the authorization modal
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState<string | null>(null);

    // Handler for delete button click
    const handleDeleteClick = (storeUrl: string) => {
        setAccountToDelete(storeUrl);
        setIsAuthModalOpen(true);
    };

    // Handler for authorization confirmation
    const handleAuthConfirm = async (authKey: string) => {
        if (!accountToDelete) return;

        try {
            // Call the delete account endpoint
            await accountService.deleteExpiredTrialAccount(accountToDelete, authKey);

            // Remove the deleted account from the list
            setAccounts(accounts.filter(account => account.storeUrl !== accountToDelete));

            // Show success toast
            toast({
                title: "Account deleted",
                description: `Account ${accountToDelete} has been deleted successfully.`,
            });

            // Reset state
            setAccountToDelete(null);
        } catch (error) {
            console.error('Error deleting account:', error);

            // Extract error message from the error object
            const errorMessage = error instanceof Error 
                ? error.message 
                : 'Failed to delete account. Please try again.';

            // Return the error message to be displayed in the modal
            throw new Error(errorMessage);
        }
    };

    // Handler for closing the authorization modal
    const handleAuthModalClose = () => {
        setIsAuthModalOpen(false);
        setAccountToDelete(null);
    };

    useEffect(() => {
        const fetchExpiredTrials = async () => {
            try {
                setLoading(true);
                const response = await accountService.getExpiredTrials();
                if (response.data.success) {
                    setAccounts(response.data.accounts);
                } else {
                    setError('Failed to fetch accounts.');
                }
            } catch (err) {
                console.error('Error fetching accounts:', err);
                setError('Error fetching accounts. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchExpiredTrials();
    }, []);

    return (
        <Layout activeMenuItem={activeMenuItem} >
            <div className="space-y-6">
                <div className="border rounded-md">
                    {loading ? (
                        <div className="p-4 text-center">Loading expired trial accounts...</div>
                    ) : error ? (
                        <div className="p-4 text-center text-red-500">{error}</div>
                    ) : accounts.length === 0 ? (
                        <div className="p-4 text-center">No expired trial accounts found.</div>
                    ) : (
                        <TrialAccountsPane 
                            accounts={accounts} 
                            showActions={true}
                            onDeleteAccount={handleDeleteClick}
                        />
                    )}
                </div>
            </div>
            <Toaster />

            {/* Authorization Modal for Delete Account */}
            <AuthorizationModal
                isOpen={isAuthModalOpen}
                onClose={handleAuthModalClose}
                onConfirm={handleAuthConfirm}
                title="Delete Account"
                description="Please enter your authorization key to confirm account deletion."
            />
        </Layout>
    )
};
